import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  AlertCircle,
  Clock,
  CheckCircle2,
  Plus,
  Users,
  Package,
  TrendingUp,
  Calendar,
} from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { Job, StockItem, Customer } from '../types';
import Layout from '../components/layout/Layout';

interface StatusCount {
  status: 'RED' | 'YELLOW' | 'GREEN';
  count: number;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState<StatusCount[]>([]);
  const [actionRequiredJobs, setActionRequiredJobs] = useState<Job[]>([]);
  const [lowStockItems, setLowStockItems] = useState<StockItem[]>([]);
  const [recentCustomers, setRecentCustomers] = useState<Customer[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statusRes, actionRes, stockRes, customerRes] = await Promise.all([
        api.get('/jobs/stats/status-counts'),
        api.get('/jobs/stats/action-required?limit=5'),
        api.get('/stock/stats/low-stock?limit=5'),
        api.get('/customers?limit=5&sort_by=created_at&sort_order=desc'),
      ]);

      if (statusRes.data.success) setStatusCounts(statusRes.data.data);
      if (actionRes.data.success) setActionRequiredJobs(actionRes.data.data);
      if (stockRes.data.success) setLowStockItems(stockRes.data.data);
      if (customerRes.data.success) setRecentCustomers(customerRes.data.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusCount = (status: string) => {
    return statusCounts.find((s) => s.status === status)?.count || 0;
  };

  const totalJobs = statusCounts.reduce((sum, s) => sum + s.count, 0);

  const stats = [
    {
      label: 'Active Jobs',
      value: totalJobs,
      icon: Briefcase,
      color: 'text-primary-500',
      bgColor: 'bg-primary-500/10',
    },
    {
      label: 'Action Required',
      value: getStatusCount('RED'),
      icon: AlertCircle,
      color: 'text-status-red',
      bgColor: 'bg-status-red/10',
    },
    {
      label: 'In Progress',
      value: getStatusCount('YELLOW'),
      icon: Clock,
      color: 'text-status-yellow',
      bgColor: 'bg-status-yellow/10',
    },
    {
      label: 'Completed',
      value: getStatusCount('GREEN'),
      icon: CheckCircle2,
      color: 'text-status-green',
      bgColor: 'bg-status-green/10',
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RED':
        return 'bg-status-red';
      case 'YELLOW':
        return 'bg-status-yellow';
      case 'GREEN':
        return 'bg-status-green';
      default:
        return 'bg-dark-muted';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Welcome */}
        <div className="mb-6">
          <h2 className="text-2xl font-semibold">Welcome back, {user?.first_name}!</h2>
          <p className="text-dark-muted">Here's what needs your attention today.</p>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => navigate('/jobs/new')}
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            New Job
          </button>
          <button
            onClick={() => navigate('/customers/new')}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Users size={20} />
            Add Customer
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {stats.map((stat) => (
                <div key={stat.label} className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-dark-muted text-sm">{stat.label}</p>
                      <p className="text-3xl font-bold mt-1">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                      <stat.icon className={stat.color} size={24} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Action Required Jobs */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="text-status-red" size={20} />
                    <h3 className="text-lg font-semibold">Action Required</h3>
                  </div>
                  <button
                    onClick={() => navigate('/jobs?status=RED')}
                    className="text-primary-500 hover:text-primary-400 text-sm"
                  >
                    View All
                  </button>
                </div>

                {actionRequiredJobs.length === 0 ? (
                  <div className="text-center py-8 text-dark-muted">
                    <CheckCircle2 size={32} className="mx-auto mb-2 text-status-green" />
                    <p>No jobs require immediate action</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {actionRequiredJobs.map((job) => (
                      <div
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="flex items-center justify-between p-4 bg-dark-elevated rounded-lg hover:bg-dark-border/50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-3 h-3 rounded-full ${getStatusColor(job.status)}`} />
                          <div>
                            <p className="font-medium">{job.title}</p>
                            <p className="text-sm text-dark-muted">{job.customer_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-mono text-dark-muted">{job.ticket_number}</p>
                          <div className="flex items-center gap-1 text-sm text-dark-muted">
                            <Calendar size={12} />
                            <span>{job.due_date ? formatDate(job.due_date) : 'No due date'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Low Stock Alert */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="text-status-yellow" size={20} />
                    <h3 className="text-lg font-semibold">Low Stock</h3>
                  </div>
                  <button
                    onClick={() => navigate('/stock?low_stock=true')}
                    className="text-primary-500 hover:text-primary-400 text-sm"
                  >
                    View All
                  </button>
                </div>

                {lowStockItems.length === 0 ? (
                  <div className="text-center py-8 text-dark-muted">
                    <TrendingUp size={32} className="mx-auto mb-2 text-status-green" />
                    <p>Stock levels are healthy</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {lowStockItems.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => navigate(`/stock/${item.id}`)}
                        className="flex items-center justify-between p-4 bg-dark-elevated rounded-lg hover:bg-dark-border/50 cursor-pointer transition-colors"
                      >
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-dark-muted font-mono">{item.sku}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-status-yellow">
                            {item.quantity_available}
                          </p>
                          <p className="text-xs text-dark-muted">
                            Reorder at {item.reorder_level}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Customers */}
              <div className="card lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="text-primary-500" size={20} />
                    <h3 className="text-lg font-semibold">Recent Customers</h3>
                  </div>
                  <button
                    onClick={() => navigate('/customers')}
                    className="text-primary-500 hover:text-primary-400 text-sm"
                  >
                    View All
                  </button>
                </div>

                {recentCustomers.length === 0 ? (
                  <div className="text-center py-8 text-dark-muted">
                    <Users size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No customers yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    {recentCustomers.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        className="p-4 bg-dark-elevated rounded-lg hover:bg-dark-border/50 cursor-pointer transition-colors"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-500 mb-2">
                          {customer.name.charAt(0).toUpperCase()}
                        </div>
                        <p className="font-medium truncate">{customer.name}</p>
                        {customer.billing_city && (
                          <p className="text-sm text-dark-muted truncate">{customer.billing_city}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
